import { getOwner } from '@ember/application';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { all } from 'rsvp';
import Route from 'cio/routes/base';
import Transition from '@ember/routing/-private/transition';

import EnvironmentService from 'cio/services/environment';
import ActionModel from 'cio/models/action';
import TemplateModel from 'cio/models/template';
import ComposerActionController from 'cio/pods/composer/action/controller';
import {
  createComposerEditableEntity,
  ComposerCampaignActionTemplate,
} from 'cio/utils/composer/composer-editable-entity';
import ConfirmChangesService from 'cio/services/confirm-changes';
import SubscriptionCenterService from 'cio/services/subscription-center';
import { ComposerSection } from 'cio/components/action-composer';

export interface ActionRouteParams {
  action_id: string;
}

export default class ActionRoute extends Route {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service confirmChanges!: ConfirmChangesService;
  @service subscriptionCenter!: SubscriptionCenterService;

  model(params: ActionRouteParams) {
    let id = params.action_id;
    let action = this.store.peekRecord('action', id);

    if (action == null) {
      return this.store.findRecord('action', id);
    }

    // Action already pre-loaded in the store may have broken template relationship.
    // In the delivery details API response, we include actions that are versions
    // of the action we're sending (for example, id 123-4). We need those versioned
    // actions for the journey steps, because those refer to the action at the time
    // the journey was in that step, while the original action may have had edits
    // since then. Loading these versioned actions breaks the relationship between
    // the "original" action and the template, so we need to reload the action.
    if (action.templateId != null && action.belongsTo('template').id() == null) {
      return action.reload();
    }

    return action;
  }

  // We want to avoid re-fetching things that already loaded in the store.
  // It will speed up things and prevent composer UI reloading on language tab change.
  afterModel(model: ActionModel) {
    let promises: PromiseLike<unknown>[] = [];
    let relationships: DS.BelongsToReference[] = [];

    let templateId = model.belongsTo('template').id();
    let template = templateId && this.store.peekRecord('template', templateId);
    if (template == null) {
      relationships.push(model.belongsTo('template'));
    }

    let campaignId = model.belongsTo('campaign').id();
    let campaign = this.store.peekRecord('campaign', campaignId);
    if (campaign == null) {
      relationships.push(model.belongsTo('campaign'));
      promises.push(this.store.findAll('collection'));
      promises.push(this.environment.loadSnippets.perform());
      promises.push(this.subscriptionCenter.initializeTask.perform());
    } else {
      if (campaign.consolidatedBranchDefaultActions.has(model.id)) {
        // load snippets with default template
        if (template == null) {
          promises.push(this.environment.loadSnippets.perform());
        }
      } else {
        // check if default template (or itself) is loaded (and subsequently, snippets)
        let defaultTemplate = campaign.getConsolidatedActionParentBranch(model.id);
        if (defaultTemplate == null) {
          promises.push(this.environment.loadSnippets.perform());
        }
      }
    }

    // Below is a weird issue, feel free to investigate it further for a better solution.

    // The afterModel function apparently can't be async here, as it causes the
    // editor to reload on language tab changes. But we can't use Promise.all here either,
    // due to an apparent Ember Data bug around loading relationships in Ember 4.12.
    // So we need to isolate the async code in a separate function.
    // More on the description here: https://github.com/customerio/ui/issues/7140
    let reloadRelationships = async (relationships: DS.BelongsToReference[]) => {
      for (let r of relationships) {
        await r.reload();
      }
    };

    // To prevent the editor from reloading on language tab changes, we need to
    // avoid running the async code if there's nothing to reload.
    if (relationships.length === 0 && promises.length === 0) {
      return;
    }

    return all([reloadRelationships(relationships), ...promises]);
  }

  setupController(controller: ComposerActionController, model: ActionModel, transition: Transition) {
    super.setupController(controller, model, transition);
    let { template, campaign } = model;

    campaign.ensureParsedTree();

    let owner = getOwner(this);
    let editableEntity = createComposerEditableEntity(owner, { campaign, action: model, template: template.content });

    // Prefetch multi-language templates to prevent composer UI reloading on language tab change.
    if (editableEntity instanceof ComposerCampaignActionTemplate) {
      editableEntity.prefetchSiblingTemplates();
    }

    controller.setProperties({
      selectedEditor: null,
      editableEntity,
    });

    controller.setEditor(template.content as TemplateModel);
    this.confirmChanges.register(controller, this);
  }
  resetController(controller: ComposerActionController, isExiting: boolean, transition: Transition) {
    super.resetController(controller, isExiting, transition);

    controller.set('currentTab', ComposerSection.Editor);
  }
}
